import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import _ from "lodash";

import type { IStore } from "src/core/shared/store";
import { setGlobalError } from "src/core/shared/store/global/global.actions";
import { IDrop } from "src/core/drops/api/drops.api";
import { MenuContext } from "src/core/shared/hooks/menu/useMenu";

import { setDrop } from "../store/drops.actions";
import { useDropsApi } from "../api/drops.api";

interface IState {
  isLoading: boolean;
  drop: IDrop | null;
  dropIdMemo?: number | null;
}

interface IActions {
  checkDropIsSynced: () => void;
}

const useDrop = (dropId?: number): [IState, IActions] => {
  const [ isLoading, setLoading ] = React.useState(true);
  const dispatch = useDispatch();
  const [ getDropResponse, getDrop ] = useDropsApi("GET_DROP");
  const [ { currentRoute } ] = React.useContext(MenuContext);
  const dropResponse = React.useMemo(() => getDropResponse.data, [ getDropResponse.data ]);

  const {
    currentDrop, currentOrder, currentList, currentRole, inMasqueradeMode
  } = useMappedState(({
    drops, orders, lists, global
  }: IStore) => ({
    currentOrder: orders.currentOrder,
    currentList: lists.currentList,
    currentDrop: drops.drop,
    currentRole: global.role,
    inMasqueradeMode: global.masqueradeMode?.active
  }));

  // get the drop details from the order or list if prossible
  const drop = React.useMemo(() => {
    if (!currentRole) {
      return null;
    }

    // if customer or on the van-sales page, just return whats in drop state
    if ((currentRole.type === "customer" || currentRoute?.path === "/van-sales")) {
      if (!inMasqueradeMode) {
        if (
          currentOrder.data?.branch
        ) {
          // when in an ordering screen, if possible get the data from the order
          return currentOrder.data.branch;
        } else if (currentList.data?.branch) {
          // when in an ordering screen, if possible get the data from the list
          return currentList.data.branch;
        }
      }

      if (currentRole.code === "customer-admin") {
        return null;
      }

      return currentDrop;
    } else {
      if (dropId) {
        // when dropid is set use the fetched data
        return dropResponse;
      } else {
        if (
          currentOrder.data &&
        currentOrder.data.branch &&
        (currentOrder.mode === "edit" || location.pathname.match(/^(\/orders\/|\/products$)/))
        ) {
          // when in an ordering screen, if possible get the data from the order
          return currentOrder.data.branch;
        } else if (
          currentList.data &&
        currentList.data.branch &&
        (currentList.mode === "edit" || location.pathname.match(/^(\/lists\/|\/products$)/))
        ) {
          // when in an ordering screen, if possible get the data from the list
          return currentList.data.branch;
        }

        if (window.location.pathname.includes("drops") && currentDrop) {
          return currentDrop;
        }
      }

      return null;
    }
  }, [
    dropResponse,
    currentRole,
    dropId,
    inMasqueradeMode,
    currentOrder.data,
    currentOrder.mode,
    currentList.data,
    currentList.mode,
    currentDrop,
    currentRoute
  ]);

  const dropIdMemo = React.useMemo(() => {
    if (!currentRole) {
      return null;
    }

    if (currentRole?.code === "customer-admin") {
      return null;
    }

    return drop?.id;
  }, [ currentRole, drop?.id ]);

  const checkDropIsSynced = React.useCallback(() => {
    if (drop?.id) {
      getDrop(drop.id);
    }
  }, [ drop?.id, getDrop ]);

  React.useEffect(() => {
    if (!dropId) {
      if (!drop) {
        setLoading(false);
      }

      setLoading(false);

      return;
    }

    if (!drop || drop.id !== dropId) {
      getDrop(dropId);
    } else {
      setLoading(false);
    }
  }, [
    drop,
    dropId,
    getDrop
  ]);

  React.useEffect(() => {
    if (getDropResponse.data) {
      dispatch(setDrop(getDropResponse.data));
      setLoading(false);
    }

    if (getDropResponse.error) {
      dispatch(setGlobalError(getDropResponse.error));
    }
  }, [
    dispatch,
    getDropResponse.data,
    getDropResponse.error
  ]);

  return [
    {
      isLoading,
      drop,
      dropIdMemo
    },
    { checkDropIsSynced }
  ];
};

export default useDrop;
